import React, { useEffect, useState } from 'react';
import {
  MessageSquareHeart,
  Loader2,
  Search,
  Star,
  CheckCircle,
  Clock,
  Eye,
  X,
  ArrowLeft,
  User,
  Calendar,
  Tag,
  Filter,
  Image,
  AlertTriangle,
  Trash2,
  Archive,
  RotateCcw,
} from 'lucide-react';
import { adminDb } from '../lib/adminDb';

type FeedbackTab = 'beta' | 'reports' | 'images';

interface Feedback {
  id: string;
  user_email: string;
  category: string;
  rating: number | null;
  message: string;
  page: string | null;
  status: 'new' | 'reviewed' | 'done' | 'archived';
  admin_note: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_CONFIG: Record<string, { label: string; badge: string; icon: React.ReactNode }> = {
  new: { label: 'Neu', badge: 'badge-warning', icon: <Clock size={12} /> },
  reviewed: { label: 'Gesehen', badge: 'badge-info', icon: <Eye size={12} /> },
  done: { label: 'Erledigt', badge: 'badge-success', icon: <CheckCircle size={12} /> },
  archived: { label: 'Archiviert', badge: 'badge-neutral', icon: <Archive size={12} /> },
};

const CATEGORY_LABELS: Record<string, string> = {
  bug: '🐛 Bug',
  feature: '💡 Feature-Wunsch',
  ui: '🎨 UI/Design',
  content: '📚 Inhalt',
  other: '💬 Sonstiges',
};

const StarRating: React.FC<{ value: number | null }> = ({ value }) => {
  if (!value) return <span className="text-xs text-muted">—</span>;
  return (
    <div style={{ display: 'flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={14}
          style={{
            fill: i <= value ? '#f59e0b' : 'transparent',
            color: i <= value ? '#f59e0b' : 'var(--text-muted)',
          }}
        />
      ))}
    </div>
  );
};

export const FeedbackPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<FeedbackTab>('beta');
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [imageFeedbacks, setImageFeedbacks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Feedback | null>(null);
  const [selectedReport, setSelectedReport] = useState<any | null>(null);
  const [selectedImage, setSelectedImage] = useState<any | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState(false);
  const [showReportsArchive, setShowReportsArchive] = useState(false);
  const [showImagesArchive, setShowImagesArchive] = useState(false);
  const [selectedReportIds, setSelectedReportIds] = useState<Set<string>>(new Set());
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    setLoading(true);
    const errors: string[] = [];
    // Beta-Feedback
    const { data: bf, error: bfErr } = await adminDb
      .from('beta_feedback')
      .select('*')
      .order('created_at', { ascending: false });
    if (bfErr) {
      console.error('[Feedback] beta_feedback error:', bfErr);
      errors.push(`Beta-Feedback: ${bfErr.message}`);
    }
    if (bf) setFeedbacks(bf);
    // Reports (support_tickets)
    const { data: st, error: stErr } = await adminDb
      .from('support_tickets')
      .select('*')
      .order('created_at', { ascending: false });
    if (stErr) {
      console.error('[Feedback] support_tickets error:', stErr);
      errors.push(`Support-Tickets: ${stErr.message}`);
    }
    if (st) setReports(st);
    // Bild-Feedback (image_feedback)
    const { data: imf, error: imfErr } = await adminDb
      .from('image_feedback')
      .select('*')
      .order('created_at', { ascending: false });
    if (imfErr) {
      console.error('[Feedback] image_feedback error:', imfErr);
      errors.push(`Bild-Feedback: ${imfErr.message}`);
    }
    if (imf) setImageFeedbacks(imf);
    if (errors.length > 0) {
      console.warn('[Feedback] Load errors:', errors);
    }
    setLoading(false);
  };

  const loadFeedbacks = loadAll;

  const filtered = React.useMemo(() => {
    let result = [...feedbacks];
    if (filterStatus) result = result.filter((f) => f.status === filterStatus);
    if (filterCategory) result = result.filter((f) => f.category === filterCategory);
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (f) => f.message.toLowerCase().includes(term) || f.user_email.toLowerCase().includes(term)
      );
    }
    return result;
  }, [feedbacks, filterStatus, filterCategory, searchTerm]);

  const handleUpdateStatus = async (feedback: Feedback, newStatus: string) => {
    setSaving(true);
    // Auto-archive when marking as done
    const finalStatus = newStatus === 'done' ? 'archived' : newStatus;
    await adminDb
      .from('beta_feedback')
      .update({
        status: finalStatus,
        admin_note: adminNote || feedback.admin_note,
        updated_at: new Date().toISOString(),
      })
      .eq('id', feedback.id);
    setSaving(false);
    setAdminNote('');
    setSelected(null);
    await loadFeedbacks();
  };

  // Bulk handlers for Reports
  const toggleReportSelect = (id: string) => {
    const next = new Set(selectedReportIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedReportIds(next);
  };
  const handleBulkDeleteReports = async () => {
    if (selectedReportIds.size === 0) return;
    if (!confirm(`${selectedReportIds.size} Report(s) endgültig löschen?`)) return;
    setBulkAction(true);
    for (const id of selectedReportIds) {
      await adminDb.from('support_tickets').delete().eq('id', id);
    }
    setSelectedReportIds(new Set());
    setBulkAction(false);
    await loadAll();
  };
  const handleBulkArchiveReports = async () => {
    if (selectedReportIds.size === 0) return;
    setBulkAction(true);
    for (const id of selectedReportIds) {
      await adminDb
        .from('support_tickets')
        .update({ status: 'archived', updated_at: new Date().toISOString() })
        .eq('id', id);
    }
    setSelectedReportIds(new Set());
    setBulkAction(false);
    await loadAll();
  };

  // Bulk handlers for Image Feedback
  const toggleImageSelect = (id: string) => {
    const next = new Set(selectedImageIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedImageIds(next);
  };
  const handleBulkDeleteImages = async () => {
    if (selectedImageIds.size === 0) return;
    if (!confirm(`${selectedImageIds.size} Bild-Feedback(s) endgültig löschen?`)) return;
    setBulkAction(true);
    for (const id of selectedImageIds) {
      await adminDb.from('image_feedback').delete().eq('id', id);
    }
    setSelectedImageIds(new Set());
    setBulkAction(false);
    await loadAll();
  };
  const handleBulkArchiveImages = async () => {
    if (selectedImageIds.size === 0) return;
    setBulkAction(true);
    for (const id of selectedImageIds) {
      await adminDb.from('image_feedback').update({ status: 'archived' }).eq('id', id);
    }
    setSelectedImageIds(new Set());
    setBulkAction(false);
    await loadAll();
  };

  const handleSaveNote = async () => {
    if (!selected || !adminNote.trim()) return;
    setSaving(true);
    await adminDb
      .from('beta_feedback')
      .update({
        admin_note: adminNote,
        status: selected.status === 'new' ? 'reviewed' : selected.status,
        updated_at: new Date().toISOString(),
      })
      .eq('id', selected.id);
    setSaving(false);
    setAdminNote('');
    setSelected(null);
    await loadFeedbacks();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Feedback endgültig löschen?')) return;
    await adminDb.from('beta_feedback').delete().eq('id', id);
    setSelected(null);
    await loadFeedbacks();
  };

  const handleArchive = async (id: string) => {
    await adminDb
      .from('beta_feedback')
      .update({ status: 'archived', updated_at: new Date().toISOString() })
      .eq('id', id);
    setSelected(null);
    await loadFeedbacks();
  };

  const handleUnarchive = async (id: string) => {
    await adminDb
      .from('beta_feedback')
      .update({ status: 'reviewed', updated_at: new Date().toISOString() })
      .eq('id', id);
    setSelected(null);
    await loadFeedbacks();
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`${selectedIds.size} Feedback(s) endgültig löschen?`)) return;
    setBulkAction(true);
    for (const id of selectedIds) {
      await adminDb.from('beta_feedback').delete().eq('id', id);
    }
    setSelectedIds(new Set());
    setBulkAction(false);
    await loadFeedbacks();
  };

  const handleBulkArchive = async () => {
    if (selectedIds.size === 0) return;
    setBulkAction(true);
    for (const id of selectedIds) {
      await adminDb
        .from('beta_feedback')
        .update({ status: 'archived', updated_at: new Date().toISOString() })
        .eq('id', id);
    }
    setSelectedIds(new Set());
    setBulkAction(false);
    await loadFeedbacks();
  };

  const nonArchived = feedbacks.filter((f) => f.status !== 'archived');
  const newCount = feedbacks.filter((f) => f.status === 'new').length;
  const reviewedCount = feedbacks.filter((f) => f.status === 'reviewed').length;
  const doneCount = feedbacks.filter((f) => f.status === 'done').length;
  const archivedCount = feedbacks.filter((f) => f.status === 'archived').length;
  const avgRating =
    nonArchived.filter((f) => f.rating).length > 0
      ? (
          nonArchived.reduce((sum, f) => sum + (f.rating || 0), 0) /
          nonArchived.filter((f) => f.rating).length
        ).toFixed(1)
      : '—';

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };
  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((f) => f.id)));
  };

  if (loading) {
    return (
      <div>
        <div className="page-header">
          <h1>
            <MessageSquareHeart size={24} /> Beta-Feedback
          </h1>
        </div>
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
        </div>
      </div>
    );
  }

  // Detail View
  if (selected) {
    const sc = STATUS_CONFIG[selected.status];
    return (
      <div>
        <div className="page-header">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setSelected(null);
              setAdminNote('');
            }}
          >
            <ArrowLeft size={14} /> Zurück
          </button>
          <div className="flex gap-2">
            {selected.status !== 'done' && selected.status !== 'archived' && (
              <button
                className="btn btn-success btn-sm"
                onClick={() => handleUpdateStatus(selected, 'done')}
              >
                <CheckCircle size={14} /> Erledigt
              </button>
            )}
            {selected.status !== 'archived' ? (
              <button
                className="btn btn-sm"
                onClick={() => handleArchive(selected.id)}
                style={{
                  background: 'rgba(107,114,128,0.1)',
                  color: '#6B7280',
                  border: '1px solid rgba(107,114,128,0.2)',
                }}
              >
                <Archive size={14} /> Archivieren
              </button>
            ) : (
              <button
                className="btn btn-sm"
                onClick={() => handleUnarchive(selected.id)}
                style={{
                  background: 'rgba(59,130,246,0.1)',
                  color: '#3B82F6',
                  border: '1px solid rgba(59,130,246,0.2)',
                }}
              >
                <RotateCcw size={14} /> Wiederherstellen
              </button>
            )}
            <button
              className="btn btn-sm"
              onClick={() => handleDelete(selected.id)}
              style={{
                background: 'rgba(239,68,68,0.1)',
                color: '#EF4444',
                border: '1px solid rgba(239,68,68,0.2)',
              }}
            >
              <Trash2 size={14} /> Löschen
            </button>
          </div>
        </div>

        <div className="card mb-4">
          <div className="flex items-center justify-between mb-3">
            <h2>{CATEGORY_LABELS[selected.category] || selected.category}</h2>
            <div className="flex gap-2 items-center">
              <StarRating value={selected.rating} />
              <span className={`badge ${sc.badge}`}>
                {sc.icon} {sc.label}
              </span>
            </div>
          </div>
          <div className="flex gap-4 text-xs text-muted mb-4">
            <span className="flex items-center gap-1">
              <User size={12} /> {selected.user_email}
            </span>
            <span className="flex items-center gap-1">
              <Calendar size={12} /> {new Date(selected.created_at).toLocaleString('de-DE')}
            </span>
            {selected.page && (
              <span className="flex items-center gap-1">
                <Tag size={12} /> Seite: {selected.page}
              </span>
            )}
          </div>

          <div
            style={{
              background: 'var(--bg-elevated)',
              padding: 16,
              borderRadius: 8,
              marginBottom: 16,
            }}
          >
            <p className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>
              Feedback:
            </p>
            <p style={{ whiteSpace: 'pre-wrap' }}>{selected.message}</p>
          </div>

          {selected.admin_note && (
            <div
              style={{
                background: 'var(--success-bg, #e8f5e9)',
                padding: 16,
                borderRadius: 8,
                marginBottom: 16,
                borderLeft: '3px solid var(--success)',
              }}
            >
              <p className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>
                Admin-Notiz:
              </p>
              <p style={{ whiteSpace: 'pre-wrap' }}>{selected.admin_note}</p>
            </div>
          )}

          <div>
            <p className="text-sm" style={{ fontWeight: 600, marginBottom: 8 }}>
              Notiz hinzufügen:
            </p>
            <textarea
              className="input"
              rows={3}
              placeholder="Interne Notiz oder Antwort..."
              value={adminNote}
              onChange={(e) => setAdminNote(e.target.value)}
            />
            <div className="flex justify-end mt-3">
              <button
                className="btn btn-primary"
                onClick={handleSaveNote}
                disabled={saving || !adminNote.trim()}
              >
                {saving ? (
                  <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <CheckCircle size={14} />
                )}
                Notiz speichern
              </button>
            </div>
          </div>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>
          <MessageSquareHeart size={24} /> Feedback
        </h1>
        <span className="text-xs text-muted">
          {feedbacks.length + reports.length + imageFeedbacks.length} Einträge
        </span>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4" style={{ flexWrap: 'wrap' }}>
        <button
          onClick={() => setActiveTab('beta')}
          className={`btn ${activeTab === 'beta' ? 'btn-primary' : 'btn-ghost'}`}
          style={{ padding: '8px 16px', fontSize: 13 }}
        >
          💬 Beta-Feedback ({feedbacks.filter((f) => f.status !== 'archived').length}/
          {feedbacks.length})
        </button>
        <button
          onClick={() => setActiveTab('reports')}
          className={`btn ${activeTab === 'reports' ? 'btn-primary' : 'btn-ghost'}`}
          style={{ padding: '8px 16px', fontSize: 13 }}
        >
          <AlertTriangle size={14} style={{ marginRight: 4 }} /> Reports (
          {reports.filter((r: any) => r.status !== 'archived').length}/{reports.length})
        </button>
        <button
          onClick={() => setActiveTab('images')}
          className={`btn ${activeTab === 'images' ? 'btn-primary' : 'btn-ghost'}`}
          style={{ padding: '8px 16px', fontSize: 13 }}
        >
          <Image size={14} style={{ marginRight: 4 }} /> Bild-Feedback (
          {imageFeedbacks.filter((f: any) => f.status !== 'archived').length}/
          {imageFeedbacks.length})
        </button>
      </div>

      {/* Reports Tab */}
      {activeTab === 'reports' &&
        (selectedReport ? (
          <div>
            <div className="page-header">
              <button className="btn btn-secondary btn-sm" onClick={() => setSelectedReport(null)}>
                <ArrowLeft size={14} /> Zurück
              </button>
              <div className="flex gap-2">
                {selectedReport.status !== 'closed' && selectedReport.status !== 'archived' && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={async () => {
                      await adminDb
                        .from('support_tickets')
                        .update({ status: 'archived', updated_at: new Date().toISOString() })
                        .eq('id', selectedReport.id);
                      setSelectedReport(null);
                      loadAll();
                    }}
                  >
                    <CheckCircle size={14} /> Schließen & Archivieren
                  </button>
                )}
                {selectedReport.status !== 'archived' ? (
                  <button
                    className="btn btn-sm"
                    onClick={async () => {
                      await adminDb
                        .from('support_tickets')
                        .update({ status: 'archived', updated_at: new Date().toISOString() })
                        .eq('id', selectedReport.id);
                      setSelectedReport(null);
                      loadAll();
                    }}
                    style={{
                      background: 'rgba(107,114,128,0.1)',
                      color: '#6B7280',
                      border: '1px solid rgba(107,114,128,0.2)',
                    }}
                  >
                    <Archive size={14} /> Archivieren
                  </button>
                ) : (
                  <button
                    className="btn btn-sm"
                    onClick={async () => {
                      await adminDb
                        .from('support_tickets')
                        .update({ status: 'open', updated_at: new Date().toISOString() })
                        .eq('id', selectedReport.id);
                      setSelectedReport(null);
                      loadAll();
                    }}
                    style={{
                      background: 'rgba(59,130,246,0.1)',
                      color: '#3B82F6',
                      border: '1px solid rgba(59,130,246,0.2)',
                    }}
                  >
                    <RotateCcw size={14} /> Wiederherstellen
                  </button>
                )}
                <button
                  className="btn btn-sm"
                  onClick={async () => {
                    if (!confirm('Report endgültig löschen?')) return;
                    await adminDb.from('support_tickets').delete().eq('id', selectedReport.id);
                    setSelectedReport(null);
                    loadAll();
                  }}
                  style={{
                    background: 'rgba(239,68,68,0.1)',
                    color: '#EF4444',
                    border: '1px solid rgba(239,68,68,0.2)',
                  }}
                >
                  <Trash2 size={14} /> Löschen
                </button>
              </div>
            </div>
            <div className="card mb-4">
              <div className="flex items-center justify-between mb-3">
                <h2>{selectedReport.subject || 'Report'}</h2>
                <span
                  className={`badge ${selectedReport.status === 'open' ? 'badge-warning' : selectedReport.status === 'archived' ? 'badge-neutral' : 'badge-success'}`}
                >
                  {selectedReport.status === 'archived' ? '📦 Archiviert' : selectedReport.status}
                </span>
              </div>
              <div className="flex gap-4 text-xs text-muted mb-4">
                <span className="flex items-center gap-1">
                  <User size={12} /> {selectedReport.user_email}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={12} />{' '}
                  {new Date(selectedReport.created_at).toLocaleString('de-DE')}
                </span>
                <span className="flex items-center gap-1">
                  <Tag size={12} /> {selectedReport.category}
                </span>
              </div>
              <div
                style={{
                  background: 'var(--bg-elevated)',
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              >
                <p className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>
                  Nachricht:
                </p>
                <p style={{ whiteSpace: 'pre-wrap' }}>{selectedReport.message}</p>
              </div>
              {selectedReport.metadata && Object.keys(selectedReport.metadata).length > 0 && (
                <div
                  style={{
                    background: 'var(--bg-elevated)',
                    padding: 16,
                    borderRadius: 8,
                    marginBottom: 16,
                    borderLeft: '3px solid var(--info)',
                  }}
                >
                  <p className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>
                    Metadaten:
                  </p>
                  <pre style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                    {JSON.stringify(selectedReport.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="table-container">
            <div className="table-toolbar">
              <div className="flex items-center gap-2">
                {selectedReportIds.size > 0 && (
                  <>
                    <span className="text-xs" style={{ fontWeight: 600 }}>
                      {selectedReportIds.size} ausgewählt
                    </span>
                    <button
                      className="btn btn-sm"
                      onClick={handleBulkArchiveReports}
                      disabled={bulkAction}
                      style={{
                        background: 'rgba(107,114,128,0.1)',
                        color: '#6B7280',
                        border: '1px solid rgba(107,114,128,0.2)',
                        fontSize: 11,
                      }}
                    >
                      <Archive size={12} /> Archivieren
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={handleBulkDeleteReports}
                      disabled={bulkAction}
                      style={{
                        background: 'rgba(239,68,68,0.1)',
                        color: '#EF4444',
                        border: '1px solid rgba(239,68,68,0.2)',
                        fontSize: 11,
                      }}
                    >
                      <Trash2 size={12} /> Löschen
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className={`btn btn-sm ${showReportsArchive ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setShowReportsArchive(!showReportsArchive)}
                  style={{ fontSize: 11 }}
                >
                  <Archive size={12} />{' '}
                  {showReportsArchive ? 'Archiv ausblenden' : 'Archiv anzeigen'}
                  {reports.filter((r: any) => r.status === 'archived').length > 0 && (
                    <span
                      style={{
                        marginLeft: 4,
                        padding: '1px 5px',
                        borderRadius: 8,
                        fontSize: 10,
                        background: 'rgba(107,114,128,0.15)',
                        fontWeight: 700,
                      }}
                    >
                      {reports.filter((r: any) => r.status === 'archived').length}
                    </span>
                  )}
                </button>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    {(() => {
                      const visibleReports = showReportsArchive
                        ? reports
                        : reports.filter((r: any) => r.status !== 'archived');
                      return (
                        <input
                          type="checkbox"
                          checked={
                            selectedReportIds.size === visibleReports.length &&
                            visibleReports.length > 0
                          }
                          onChange={() => {
                            const visR = showReportsArchive
                              ? reports
                              : reports.filter((r: any) => r.status !== 'archived');
                            if (selectedReportIds.size === visR.length)
                              setSelectedReportIds(new Set());
                            else setSelectedReportIds(new Set(visR.map((r: any) => r.id)));
                          }}
                          title="Alle auswählen"
                        />
                      );
                    })()}
                  </th>
                  <th>Status</th>
                  <th>Kategorie</th>
                  <th>Betreff</th>
                  <th>Nachricht</th>
                  <th>Nutzer</th>
                  <th>Datum</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const visibleReports = showReportsArchive
                    ? reports
                    : reports.filter((r: any) => r.status !== 'archived');
                  return visibleReports.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}
                      >
                        Keine Reports.
                      </td>
                    </tr>
                  ) : (
                    visibleReports.map((r: any) => (
                      <tr
                        key={r.id}
                        style={{ cursor: 'pointer', opacity: r.status === 'archived' ? 0.5 : 1 }}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedReportIds.has(r.id)}
                            onChange={() => toggleReportSelect(r.id)}
                          />
                        </td>
                        <td onClick={() => setSelectedReport(r)}>
                          <span
                            className={`badge ${r.status === 'open' ? 'badge-warning' : r.status === 'closed' ? 'badge-success' : r.status === 'archived' ? 'badge-neutral' : 'badge-info'}`}
                          >
                            {r.status === 'archived' ? '📦 Archiv' : r.status}
                          </span>
                        </td>
                        <td onClick={() => setSelectedReport(r)}>
                          <span className="badge badge-neutral">{r.category}</span>
                        </td>
                        <td
                          onClick={() => setSelectedReport(r)}
                          style={{
                            maxWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {r.subject}
                        </td>
                        <td
                          onClick={() => setSelectedReport(r)}
                          style={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {r.message}
                        </td>
                        <td onClick={() => setSelectedReport(r)} className="text-sm">
                          {r.user_email}
                        </td>
                        <td onClick={() => setSelectedReport(r)} className="text-xs text-muted">
                          {new Date(r.created_at).toLocaleDateString('de-DE')}
                        </td>
                      </tr>
                    ))
                  );
                })()}
              </tbody>
            </table>
          </div>
        ))}

      {/* Image Feedback Tab */}
      {activeTab === 'images' &&
        (selectedImage ? (
          <div>
            <div className="page-header">
              <button className="btn btn-secondary btn-sm" onClick={() => setSelectedImage(null)}>
                <ArrowLeft size={14} /> Zurück
              </button>
              <div className="flex gap-2">
                {selectedImage.status !== 'resolved' && selectedImage.status !== 'archived' && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={async () => {
                      await adminDb
                        .from('image_feedback')
                        .update({ status: 'archived' })
                        .eq('id', selectedImage.id);
                      setSelectedImage(null);
                      loadAll();
                    }}
                  >
                    <CheckCircle size={14} /> Gelöst & Archivieren
                  </button>
                )}
                {selectedImage.status !== 'archived' ? (
                  <button
                    className="btn btn-sm"
                    onClick={async () => {
                      await adminDb
                        .from('image_feedback')
                        .update({ status: 'archived' })
                        .eq('id', selectedImage.id);
                      setSelectedImage(null);
                      loadAll();
                    }}
                    style={{
                      background: 'rgba(107,114,128,0.1)',
                      color: '#6B7280',
                      border: '1px solid rgba(107,114,128,0.2)',
                    }}
                  >
                    <Archive size={14} /> Archivieren
                  </button>
                ) : (
                  <button
                    className="btn btn-sm"
                    onClick={async () => {
                      await adminDb
                        .from('image_feedback')
                        .update({ status: 'open' })
                        .eq('id', selectedImage.id);
                      setSelectedImage(null);
                      loadAll();
                    }}
                    style={{
                      background: 'rgba(59,130,246,0.1)',
                      color: '#3B82F6',
                      border: '1px solid rgba(59,130,246,0.2)',
                    }}
                  >
                    <RotateCcw size={14} /> Wiederherstellen
                  </button>
                )}
                <button
                  className="btn btn-sm"
                  onClick={async () => {
                    if (!confirm('Bild-Feedback endgültig löschen?')) return;
                    await adminDb.from('image_feedback').delete().eq('id', selectedImage.id);
                    setSelectedImage(null);
                    loadAll();
                  }}
                  style={{
                    background: 'rgba(239,68,68,0.1)',
                    color: '#EF4444',
                    border: '1px solid rgba(239,68,68,0.2)',
                  }}
                >
                  <Trash2 size={14} /> Löschen
                </button>
              </div>
            </div>
            <div className="card mb-4">
              <div className="flex items-center justify-between mb-3">
                <h2>Bild-Feedback: {selectedImage.question_id}</h2>
                <span
                  className={`badge ${selectedImage.status === 'resolved' ? 'badge-success' : selectedImage.status === 'archived' ? 'badge-neutral' : 'badge-warning'}`}
                >
                  {selectedImage.status === 'archived'
                    ? '📦 Archiviert'
                    : selectedImage.status || 'open'}
                </span>
              </div>
              <div className="flex gap-4 text-xs text-muted mb-4">
                <span className="flex items-center gap-1">
                  <User size={12} /> {selectedImage.user_id || 'Anonym'}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar size={12} />{' '}
                  {new Date(selectedImage.created_at).toLocaleString('de-DE')}
                </span>
                <span className="flex items-center gap-1">
                  <Tag size={12} /> Grund: {selectedImage.reason || '—'}
                </span>
              </div>
              <div
                style={{
                  background: 'var(--bg-elevated)',
                  padding: 16,
                  borderRadius: 8,
                  marginBottom: 16,
                }}
              >
                <p className="text-sm" style={{ fontWeight: 600, marginBottom: 4 }}>
                  Kommentar:
                </p>
                <p style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedImage.comment || 'Kein Kommentar'}
                </p>
              </div>
              {selectedImage.image_url && (
                <div style={{ marginBottom: 16 }}>
                  <p className="text-sm" style={{ fontWeight: 600, marginBottom: 8 }}>
                    Gemeldetes Bild:
                  </p>
                  <img
                    src={selectedImage.image_url}
                    alt="Gemeldetes Bild"
                    style={{
                      maxWidth: '100%',
                      maxHeight: 300,
                      borderRadius: 8,
                      border: '1px solid var(--border)',
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="table-container">
            <div className="table-toolbar">
              <div className="flex items-center gap-2">
                {selectedImageIds.size > 0 && (
                  <>
                    <span className="text-xs" style={{ fontWeight: 600 }}>
                      {selectedImageIds.size} ausgewählt
                    </span>
                    <button
                      className="btn btn-sm"
                      onClick={handleBulkArchiveImages}
                      disabled={bulkAction}
                      style={{
                        background: 'rgba(107,114,128,0.1)',
                        color: '#6B7280',
                        border: '1px solid rgba(107,114,128,0.2)',
                        fontSize: 11,
                      }}
                    >
                      <Archive size={12} /> Archivieren
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={handleBulkDeleteImages}
                      disabled={bulkAction}
                      style={{
                        background: 'rgba(239,68,68,0.1)',
                        color: '#EF4444',
                        border: '1px solid rgba(239,68,68,0.2)',
                        fontSize: 11,
                      }}
                    >
                      <Trash2 size={12} /> Löschen
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button
                  className={`btn btn-sm ${showImagesArchive ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => setShowImagesArchive(!showImagesArchive)}
                  style={{ fontSize: 11 }}
                >
                  <Archive size={12} />{' '}
                  {showImagesArchive ? 'Archiv ausblenden' : 'Archiv anzeigen'}
                  {imageFeedbacks.filter((f: any) => f.status === 'archived').length > 0 && (
                    <span
                      style={{
                        marginLeft: 4,
                        padding: '1px 5px',
                        borderRadius: 8,
                        fontSize: 10,
                        background: 'rgba(107,114,128,0.15)',
                        fontWeight: 700,
                      }}
                    >
                      {imageFeedbacks.filter((f: any) => f.status === 'archived').length}
                    </span>
                  )}
                </button>
              </div>
            </div>
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    {(() => {
                      const visibleImages = showImagesArchive
                        ? imageFeedbacks
                        : imageFeedbacks.filter((f: any) => f.status !== 'archived');
                      return (
                        <input
                          type="checkbox"
                          checked={
                            selectedImageIds.size === visibleImages.length &&
                            visibleImages.length > 0
                          }
                          onChange={() => {
                            const visI = showImagesArchive
                              ? imageFeedbacks
                              : imageFeedbacks.filter((f: any) => f.status !== 'archived');
                            if (selectedImageIds.size === visI.length)
                              setSelectedImageIds(new Set());
                            else setSelectedImageIds(new Set(visI.map((f: any) => f.id)));
                          }}
                          title="Alle auswählen"
                        />
                      );
                    })()}
                  </th>
                  <th>Status</th>
                  <th>Frage-ID</th>
                  <th>Grund</th>
                  <th>Kommentar</th>
                  <th>Nutzer</th>
                  <th>Datum</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const visibleImages = showImagesArchive
                    ? imageFeedbacks
                    : imageFeedbacks.filter((f: any) => f.status !== 'archived');
                  return visibleImages.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}
                      >
                        Keine Bild-Feedbacks.
                      </td>
                    </tr>
                  ) : (
                    visibleImages.map((f: any) => (
                      <tr
                        key={f.id}
                        style={{ cursor: 'pointer', opacity: f.status === 'archived' ? 0.5 : 1 }}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedImageIds.has(f.id)}
                            onChange={() => toggleImageSelect(f.id)}
                          />
                        </td>
                        <td onClick={() => setSelectedImage(f)}>
                          <span
                            className={`badge ${f.status === 'open' ? 'badge-warning' : f.status === 'resolved' ? 'badge-success' : f.status === 'archived' ? 'badge-neutral' : 'badge-info'}`}
                          >
                            {f.status === 'archived' ? '📦 Archiv' : f.status || 'open'}
                          </span>
                        </td>
                        <td onClick={() => setSelectedImage(f)} className="text-xs">
                          {f.question_id?.slice(0, 8)}...
                        </td>
                        <td onClick={() => setSelectedImage(f)}>
                          <span className="badge badge-neutral">{f.reason || '—'}</span>
                        </td>
                        <td
                          onClick={() => setSelectedImage(f)}
                          style={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {f.comment || '—'}
                        </td>
                        <td onClick={() => setSelectedImage(f)} className="text-sm">
                          {f.user_id?.slice(0, 8) || '—'}
                        </td>
                        <td onClick={() => setSelectedImage(f)} className="text-xs text-muted">
                          {new Date(f.created_at).toLocaleDateString('de-DE')}
                        </td>
                      </tr>
                    ))
                  );
                })()}
              </tbody>
            </table>
          </div>
        ))}

      {/* Beta-Feedback Tab (original) */}
      {activeTab === 'beta' && (
        <>
          {/* KPIs */}
          <div className="grid-cols-4 mb-6">
            <div className="kpi-card">
              <span className="kpi-label">Gesamt</span>
              <span className="kpi-value">{feedbacks.length}</span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Neu</span>
              <span
                className="kpi-value"
                style={{ color: newCount > 0 ? 'var(--warning)' : 'var(--success)' }}
              >
                {newCount}
              </span>
            </div>
            <div className="kpi-card">
              <span className="kpi-label">Gesehen</span>
              <span className="kpi-value" style={{ color: 'var(--info)' }}>
                {reviewedCount}
              </span>
            </div>
            <div className="kpi-card">
              <span
                className="kpi-label"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Star size={12} style={{ fill: '#f59e0b', color: '#f59e0b' }} /> Durchschnitt
              </span>
              <span className="kpi-value">{avgRating}</span>
            </div>
          </div>

          {/* Filters + Table */}
          <div className="table-container">
            <div className="table-toolbar">
              <div className="flex gap-3 items-center" style={{ flex: 1, flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', minWidth: 200 }}>
                  <Search
                    size={16}
                    style={{
                      position: 'absolute',
                      left: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: 'var(--text-muted)',
                    }}
                  />
                  <input
                    className="input input-search"
                    placeholder="Suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <select
                  className="input"
                  style={{ width: 130 }}
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">Alle Status</option>
                  <option value="new">Neu</option>
                  <option value="reviewed">Gesehen</option>
                  <option value="done">Erledigt</option>
                  <option value="archived">Archiviert</option>
                </select>
                <select
                  className="input"
                  style={{ width: 150 }}
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                >
                  <option value="">Alle Kategorien</option>
                  <option value="bug">🐛 Bug</option>
                  <option value="feature">💡 Feature</option>
                  <option value="ui">🎨 UI/Design</option>
                  <option value="content">📚 Inhalt</option>
                  <option value="other">💬 Sonstiges</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                {selectedIds.size > 0 && (
                  <>
                    <span className="text-xs" style={{ fontWeight: 600 }}>
                      {selectedIds.size} ausgewählt
                    </span>
                    <button
                      className="btn btn-sm"
                      onClick={handleBulkArchive}
                      disabled={bulkAction}
                      style={{
                        background: 'rgba(107,114,128,0.1)',
                        color: '#6B7280',
                        border: '1px solid rgba(107,114,128,0.2)',
                        fontSize: 11,
                      }}
                    >
                      <Archive size={12} /> Archivieren
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={handleBulkDelete}
                      disabled={bulkAction}
                      style={{
                        background: 'rgba(239,68,68,0.1)',
                        color: '#EF4444',
                        border: '1px solid rgba(239,68,68,0.2)',
                        fontSize: 11,
                      }}
                    >
                      <Trash2 size={12} /> Löschen
                    </button>
                  </>
                )}
                <span className="text-xs text-muted">{filtered.length} Ergebnisse</span>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.size === filtered.length && filtered.length > 0}
                      onChange={toggleAll}
                      title="Alle auswählen"
                    />
                  </th>
                  <th>Status</th>
                  <th>Kategorie</th>
                  <th>Bewertung</th>
                  <th>Feedback</th>
                  <th>Nutzer</th>
                  <th>Datum</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}
                    >
                      {feedbacks.length === 0
                        ? 'Noch kein Beta-Feedback eingegangen.'
                        : 'Keine Treffer.'}
                    </td>
                  </tr>
                ) : (
                  filtered.map((f) => {
                    const sc = STATUS_CONFIG[f.status] || STATUS_CONFIG.new;
                    return (
                      <tr
                        key={f.id}
                        style={{ cursor: 'pointer', opacity: f.status === 'archived' ? 0.6 : 1 }}
                      >
                        <td onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(f.id)}
                            onChange={() => toggleSelect(f.id)}
                          />
                        </td>
                        <td
                          onClick={() => {
                            setSelected(f);
                            setAdminNote(f.admin_note || '');
                          }}
                        >
                          <span className={`badge ${sc.badge}`}>
                            {sc.icon} {sc.label}
                          </span>
                        </td>
                        <td
                          onClick={() => {
                            setSelected(f);
                            setAdminNote(f.admin_note || '');
                          }}
                        >
                          <span className="badge badge-neutral">
                            {CATEGORY_LABELS[f.category] || f.category}
                          </span>
                        </td>
                        <td
                          onClick={() => {
                            setSelected(f);
                            setAdminNote(f.admin_note || '');
                          }}
                        >
                          <StarRating value={f.rating} />
                        </td>
                        <td
                          onClick={() => {
                            setSelected(f);
                            setAdminNote(f.admin_note || '');
                          }}
                          style={{
                            maxWidth: 300,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {f.message}
                        </td>
                        <td
                          onClick={() => {
                            setSelected(f);
                            setAdminNote(f.admin_note || '');
                          }}
                          className="text-sm"
                        >
                          {f.user_email}
                        </td>
                        <td
                          onClick={() => {
                            setSelected(f);
                            setAdminNote(f.admin_note || '');
                          }}
                          className="text-xs text-muted"
                        >
                          {new Date(f.created_at).toLocaleDateString('de-DE')}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
